import * as React from 'react';
import { Plan, PlanPackageCriterion, PlanTier } from 'fs-api-client';
import { CUSTOM_LITE_TYPE, PlanPackage, TierProp } from './BillingPlanModel';
import { Providers } from 'portal-shared-lib';
// import { Providers } from 'portal-shared-lib';

const SubscriptionType = PlanPackageCriterion.SubscriptionTypeEnum;

const FilespaceTypeMap = {
    [SubscriptionType.Advanced]: 'Advanced Filespace',
    [SubscriptionType.Basic]: 'Basic Filespace',
    [SubscriptionType.Custom]: 'Custom Filespace',
    [SubscriptionType.Any]: 'Any Filespace',
    [SubscriptionType.Enterprise]: 'Enterprise Filespace',
    [SubscriptionType.Teams]: 'Teams Filespace',
    [SubscriptionType.None]: 'Legacy Filespace'
};

interface PlanPackageEditProps {
    planPackage: PlanPackage;
    isDeleted: boolean;
    isNewlyCreated: boolean;
    selectedPlan: Plan;

    onPlanTierModified: (
        newValue: string,
        planPackId: string,
        planDimension: Plan.DimensionEnum,
        tierIdx: number,
        tierProp: TierProp) => void;
    onPlanTierRemoved: (planPackId: string, planDimension: Plan.DimensionEnum, planTierIdx: number) => void;
    onPlanPackageRemoved: (id: string) => void;
    onPlanPackStorageOwnerChange: (id: string, criterionIdx: number, newValue: string) => void;
    onPlanPackStorageProviderChange: (id: string, criterionIdx: number, storageProvider: string) => void;
    onPlanPackSubscriptionTypeChange: (planPackId: string, criterionIdx: number, subscriptionType: string) => void;
    onSubscriptionTypeAdded: (planPackId: string, subscriptionType: PlanPackageCriterion.SubscriptionTypeEnum) => void;
    onSubscriptionTypeRemoved: (planPackId: string, criterionIdx: number) => void;
    onProviderAdded: (planPackId: string) => void;
    onProviderRemoved: (planPackId: string, criterionIdx: number) => void;
    onProviderChange: (planPackId: string, criterionIdx: number, newProvider: Providers) => void;
    onPlanDimensionChanged: (
        oldDimension: Plan.DimensionEnum,
        newDimension: Plan.DimensionEnum,
        planPackId: string) => void;
    onPlanTierAdded: (planPackId: string, planDimension: Plan.DimensionEnum) => void;
    onCancel: () => void;
    onApply: () => void;
}

interface PlanPackageEditState {

}

function getFilespaceTypeHeader(planPackage: PlanPackage) {
    if (planPackage.isLite) {
        return `Custom Lite Filespace`;
    }

    if (planPackage.includeCriteria.length > 1) {
        return 'Multiple Filespaces';
    }

    return FilespaceTypeMap[planPackage.includeCriteria[0].subscriptionType];
}

export class PlanPackageEdit extends React.Component<PlanPackageEditProps, PlanPackageEditState> {

    constructor(props: PlanPackageEditProps) {
        super(props);
    }

    render() {
        const id = this.props.planPackage.id;
        const planPackage = this.props.planPackage;
        const header = getFilespaceTypeHeader(planPackage);
        return (
            <div>
                <div className="row dmb">
                    <div className="fullWidth">
                        <div className="spaceBetween vcenter">
                            <div>
                                <h2>{header}</h2>
                            </div>
                            <div className="row buttons right">
                                <button className="light" onClick={this.props.onCancel}>
                                    Cancel
                                </button>
                                <button onClick={this.props.onApply}>
                                    Save draft and preview
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row" key={id}>
                    <div className="col-2 rightPaddingOnly rightSeparator">
                        <label>Subscription type</label>
                        {this.renderSubscriptionTypes(planPackage)}
                        <button className="light" onClick={() => this.props.onSubscriptionTypeAdded(id, PlanPackageCriterion.SubscriptionTypeEnum.Any)}>Add type</button>
                        <br/>
                        {planPackage.isLite && <div>
                            <label>Storage providers</label>
                            {this.renderProviders(planPackage)}
                            {planPackage.includeCriteria.length < 2 && <button className="light" onClick={() => this.props.onProviderAdded(id)}>Add provider</button>}
                        </div>}
                    </div>
                    <div className="col-5 leftMarginOnly">
                        {this.renderPlan(this.props.selectedPlan, id)}
                    </div>
                </div>
            </div>
        );
    }

    private renderProviders(
        planPackage: PlanPackage,
    ): JSX.Element {

        const showDeleteButton = planPackage.includeCriteria.length > 1;
        return (
            <div>
                {planPackage.includeCriteria.map(criterion => this.renderProvider(planPackage.id, criterion, showDeleteButton))}
            </div>
        );
    }

    private renderProvider(
        planPackId: string,
        criterion: PlanPackageCriterion,
        showDeleteButton: boolean
    ): JSX.Element {
        const selectedValue = criterion.storageProvider;
        return (
            <div className="row">
                <select value={selectedValue} style={{marginBottom: '10px'}} onChange={(e) => this.props.onProviderChange(planPackId, 0, e.target.value as Providers)}>
                    <option value="Wasabi">wasabi</option>
                    <option value="Backblaze">backblaze</option>
                </select>
                {showDeleteButton ? <button className="warning" onClick={() => this.props.onProviderRemoved(planPackId, 0)}>Remove</button> : null}
            </div>
        );
    }

    private renderSubscriptionTypes(planPackage: PlanPackage) {
        const id = planPackage.id;

        if (planPackage.isLite) {
            const criterion = planPackage.includeCriteria[0];
            const index = 0;
            return this.renderSubscriptionType(criterion.subscriptionType, id, index, false, criterion.storageProvider as Providers, planPackage.isLite);
        }

        const showDeleteButton = planPackage.includeCriteria.length > 1;
        return planPackage.includeCriteria.map(
                (criterion, index) => this.renderSubscriptionType(criterion.subscriptionType, id, index, showDeleteButton, criterion.storageProvider as Providers, planPackage.isLite));
    }

    private renderSubscriptionType(
        value: PlanPackageCriterion.SubscriptionTypeEnum,
        planPackId: string,
        criterionIdx: number,
        showDeleteButton: boolean,
        provider: Providers,
        isLite: boolean,
    ): JSX.Element {
        let selectedValue = value.toString();
        if (value === PlanPackageCriterion.SubscriptionTypeEnum.Advanced) {
            if (provider === Providers.AWS) {
                selectedValue = `${selectedValue}_aws`;
            } else if (provider === Providers.IBM) {
                selectedValue = `${selectedValue}_ibm`;
            } else {
                throw new Error(`Invalid combination of storage provider ${provider} and subscription type ${value}`);
            }
        } else if (isLite) {
            selectedValue = CUSTOM_LITE_TYPE;
        }

        return (
            <div className="row">
                <select value={selectedValue} style={{marginBottom: '10px'}} onChange={(e) => this.props.onPlanPackSubscriptionTypeChange(planPackId, criterionIdx, e.target.value)}>
                    <option value="basic">basic</option>
                    <option value="teams">teams</option>
                    <option value="enterprise">enterprise</option>
                    <option value="advanced_ibm">advanced_ibm</option>
                    <option value="advanced_aws">advanced_aws</option>
                    <option value="custom">custom</option>
                    <option value="none">none</option>
                    <option value="custom_lite">custom_lite</option>
                    <option value="any">any</option>
                </select>
                {showDeleteButton ? <button className="warning" onClick={() => this.props.onSubscriptionTypeRemoved(planPackId, criterionIdx)}>Remove</button> : null}
            </div>
        );
    }

    private renderPlan(plan: Plan, planPackId: string): JSX.Element {
        return (
            <React.Fragment>
                <label>Plan Dimension</label>
                {this.renderPlanDimensionEnum(plan.dimension, planPackId)}
                {plan.tiers.map((tier, idx) =>
                                    this.renderPlanTier(planPackId, plan.dimension, tier, idx, plan.tiers.length, plan.tiers.length > 1))}
                {plan.tiers.length < 2 && <div className="row bottomAligned pt">
                    <button className="light fullWidth" onClick={() => this.props.onPlanTierAdded(planPackId, plan.dimension)}>Add tier</button>
                </div>}
            </React.Fragment>);
    }

    private getPossibleDimensions(): string [] {
        const dimensionArray = [];
        for (const dimension in Plan.DimensionEnum) {
            if (!Plan.DimensionEnum.hasOwnProperty(dimension)) {
                continue;
            }
            dimensionArray.push(dimension);
        }
        return dimensionArray;
    }

    private renderPlanDimensionEnum(planDimension: Plan.DimensionEnum, planPackId: string): JSX.Element {
        const dimensionArray = this.getPossibleDimensions();
        return (
            <select value={planDimension} onChange={(e) => this.props.onPlanDimensionChanged(planDimension, e.target.value as any, planPackId)}>
                {dimensionArray.map(dimension => <option key={dimension} value={dimension}>{dimension}</option>)}
            </select>
        );
    }

    private renderPlanTier(
        planPackId: string,
        planDimension: Plan.DimensionEnum,
        tier: PlanTier,
        planTierIdx: number,
        tiersLength: number,
        canRemove: boolean
    ): JSX.Element {
        return (
            <div className="row bottomAligned inner" key={planTierIdx}>
                {tiersLength > 1 && (<div className="col-1">
                    <label htmlFor="field-1">From</label>
                    <input className="storage" value={tier.from} onChange={(e) => this.props.onPlanTierModified(e.target.value, planPackId, planDimension, planTierIdx, TierProp.From)} autoFocus={false} type="text" name="From" id="field-1"/> 
                </div>)}

                {tiersLength > 1 && (<div className="col-1">
                    <label htmlFor="field-2">Up to</label>
                    <input className="storage" value={tier.upTo ? tier.upTo : 'inf'} onChange={(e) => this.props.onPlanTierModified(e.target.value, planPackId, planDimension, planTierIdx, TierProp.UpTo)} autoFocus={false} type="text" name="Up to" id="field-2"/>
                </div>)}

                <div className="col-1">
                    <label htmlFor="field-3">Price per unit</label>
                    <input className="money" value={tier.pricePerUnit} onChange={(e) => this.props.onPlanTierModified(e.target.value, planPackId, planDimension, planTierIdx, TierProp.PricePerUnit)} autoFocus={false} type="text" name="Price per unit" id="field-3"/>
                </div>

                {tiersLength > 1 && (<div className="col-1">
                    <label htmlFor="field-4">Flat price</label>
                    <input className="money" value={tier.flatPrice} onChange={(e) => this.props.onPlanTierModified(e.target.value, planPackId, planDimension, planTierIdx, TierProp.FlatPrice)} autoFocus={false} type="text" name="Flat price" id="field-4"/>
                </div>)}
                {canRemove && 
                <div className="flexEnd hmb">
                    <button className="warning" onClick={() => this.props.onPlanTierRemoved(planPackId, planDimension, planTierIdx)}>Remove</button>
                </div>}
            </div>
        );
    }
}


// WEBPACK FOOTER //
// ./src/backoffice/components/billing/PlanPackageEdit.tsx